<template>
  <v-row>
    <v-col cols="12">
      <v-text-field
          :label="`${dictionaryTypeName}トークン`"
          v-model="token"
          @input="handleImportTokenInput"
          :error="error"
          :error-messages="errorMessage"
          spellcheck="false"
          autofocus
          outlined>
        <template #append>
          <el-tooltip content="クリップボードからペースト" placement="bottom" v-if="navigator.clipboard">
            <v-btn
                @click="handleImportTokenPaste"
                :class="{shake: shakePaste}"
                icon>
              <v-icon>mdi-content-paste</v-icon>
            </v-btn>
          </el-tooltip>
        </template>
      </v-text-field>
    </v-col>
    <v-col cols="12">
      <text-field-like
          v-if="dictionaryType === 'common'"
          :value="dictionary.name"
          label="辞書名"
          :placeholder="placeholder"
          :loading="loading">
      </text-field-like>
      <v-text-field
          v-else
          v-model="dictionary.name"
          label="辞書名"
          :placeholder="placeholder"
          :loading="loading"
          :readonly="error"
          messages="確定後は変更できません"
          :rules="[v => error || !!v || '辞書名を入力してください']">
      </v-text-field>
    </v-col>
    <v-col cols="12">
      <text-field-like
          v-if="dictionaryType === 'common'"
          :value="dictionary.description"
          label="説明"
          :placeholder="placeholder"
          :loading="loading"
          multi-line>
      </text-field-like>
      <v-textarea
          v-else
          v-model="dictionary.description"
          label="説明"
          :placeholder="placeholder"
          :loading="loading"
          :readonly="error"
          messages="複数行入力可・確定後は変更できません"
          rows="1"
          auto-grow>
      </v-textarea>
    </v-col>
    <v-col cols="12">
      <owner-field
          label="作成事業者"
          :placeholder="placeholder"
          :value="dictionary.ownerId"
          hide-detail>
      </owner-field>
    </v-col>
  </v-row>
</template>

<script>
import TextFieldLike from "@/components/molecules/TextFieldLike.vue";
import OwnerField from "@/components/molecules/OwnerField.vue";
import gql from "graphql-tag";
import {DICTIONARY_TYPE_NAMES} from "@/constants";
import {mapActions, mapGetters, mapMutations} from "vuex";
export default {
  name: "TokenImportForm",
  components: {OwnerField, TextFieldLike},
  inject: {
    dialog: {default: null},
  },
  props: {
    dictionaryType: {
      type: String,
      required: true,
    }
  },
  data() {
    return {
      token: '',
      tokenIsValid: false,
      dictionary: {},
      error: false,
      errorMessage: '',
      loading: false,
      shakePaste: false,
      navigator: window.navigator,
      applyHandler: () => {
        this.apply();
      },
      resetHandler: () => {
        this.reset();
      },
    };
  },
  created() {
    this.dialog.vm.$on('apply', this.applyHandler);
    this.dialog.vm.$on('close', this.resetHandler);
  },
  beforeDestroy() {
    this.dialog.vm.$off('apply', this.applyHandler);
    this.dialog.vm.$off('close', this.resetHandler);
  },
  computed: {
    ...mapGetters({
      account: 'hcs/account',
      hcsClient: 'hcs/client',
      entities: 'hds/entities',
      hdsClient: 'hds/apiClient',
    }),
    placeholder() {
      return this.tokenIsValid ? '未設定' : '-';
    },
    dictionaryTypeName() {
      return DICTIONARY_TYPE_NAMES[this.dictionaryType] ?? '辞書';
    }
  },
  methods: {
    ...mapMutations({
      showSnackbar: 'showSnackbar'
    }),
    ...mapActions({
      gqlQuery: 'hds/gqlQuery',
      gqlQueryAndLoad: 'hds/gqlQueryAndLoad',
      gqlMutate: 'hds/gqlMutate',
      gqlMutateAndLoad: 'hds/gqlMutateAndLoad'
    }),
    async handleImportTokenInput() {
      let dictionary = {};
      let error = false;
      let errorMessage = '';
      let tokenIsValid = false;
      const token = this.token;
      if(token && token.match(/^[0-9a-zA-Z]{20}$/)) {
        try {
          this.loading = true;
          const {dictionaryByToken} = await this.gqlQuery({
            query: gql`
            query FindDictionaryByToken(
                $token: String!,
            ) {
                dictionaryByToken(token: $token) {
                    id
                    name
                    description
                    dictionaryType
                    ownerId
                    sourceDictionaryId
                }
            }`,
            variables: {
              token
            }
          });
          if(dictionaryByToken) {
            tokenIsValid = true;
            dictionary = dictionaryByToken;
            if(dictionary.ownerId === this.account?.owner.id) {
              error = true;
              errorMessage = "自事業者の辞書のインポートはできません";
            } else if((dictionary.dictionaryType === 'local' && this.entities.dictionary.findOne({sourceDictionaryId: dictionary.id})) ||
                (dictionary.dictionaryType === 'common' && this.entities.dictionary.findOne({id: dictionary.id}))) {
              error = true;
              errorMessage = "既にインポート済みの辞書です";
              console.log(this.entities.dictionary.findOne({sourceDictionaryId: dictionary.id}))
            } else if((dictionary.dictionaryType !== 'local' && this.dictionaryType === 'external') ||
                (dictionary.dictionaryType !== 'common' && this.dictionaryType === 'common')) {
              error = true;
              errorMessage = `ここで${DICTIONARY_TYPE_NAMES[dictionary.dictionaryType]}のインポートはできません`;
            }
          } else {
            dictionary = {};
            error = true;
            errorMessage = "存在しないトークンです";
          }
        } finally {
          this.loading = false;
        }
      } else {
        dictionary = {};
        error = true;
        errorMessage = '正しくないトークンです';
      }

      this.tokenIsValid = tokenIsValid;
      this.dictionary = dictionary;
      this.error = error;
      this.errorMessage = errorMessage;
    },
    async handleImportTokenPaste() {
      const text = await navigator.clipboard.readText();
      if(text.match(/^[0-9a-zA-Z]{20}$/)) {
        this.token = text;
        await this.handleImportTokenInput();
      } else {
        this.shakePaste = true;
        setTimeout(() => {
          this.shakePaste = false;
        }, 200);
      }
    },
    async apply() {
      if(this.dictionaryType === 'common') {
        await this.gqlMutateAndLoad({
          mutation: gql`
              mutation ImportDictionary(
                $token: String!
              ) {
                dictionary: useDictionaryToken(token: $token) {
                    id
                    name
                    description
                    dictionaryType
                    ownerId
                    sourceDictionaryId
                    sourceDictionary {
                        ownerId
                    }
                }
              }`,
          variables: {
            token: this.token,
          },
          mapping: {
            dictionary: 'dictionary*'
          }
        });
      }
      if(this.dictionaryType === 'external') {
        await this.gqlMutateAndLoad({
          mutation: gql`
              mutation ImportDictionary(
                $token: String!,
                $params: DictionaryImportParamsInput
              ) {
                dictionary: importDictionary(token: $token, params: $params) {
                    id
                    name
                    description
                    dictionaryType
                    ownerId
                    sourceDictionaryId
                    sourceDictionary {
                        ownerId
                    }
                }
              }`,
          variables: {
            token: this.token,
            params: {
              name: this.dictionary.name,
              description: this.dictionary.description,
            }
          },
          mapping: {
            dictionary: 'dictionary*'
          }
        });
      }
      this.showSnackbar({
        message: `${DICTIONARY_TYPE_NAMES[this.dictionary.dictionaryType]}「${this.dictionary.name}」のインポートが完了しました`
      });
      this.$emit('after-apply', this.dictionaryType);
    },
    reset() {
      this.token = '';
      this.tokenIsValid = false;
      this.dictionary = {};
      this.error = false;
      this.errorMessage = '';
      this.loading = false;
    }
  }
}
</script>

<style lang="scss" scoped>
@import "~csshake";
</style>
